import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useMatch } from "react-router-dom";
import { ContactSidebar } from "./ContactSidebar/ContactSidebar";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import UserContext from "../../../contexts/UserContext";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnGetContactsDTO from "../../../models/DTO/IOnGetContactsDTO";
import OwTable from "../../shared/OwTable/OwTable";
import IContact from "../../../models/IContact";
import IOwTableCols from "../../../models/IOwTableCols";
import OwButton from "../../shared/OwButton/OwButton";
import OwGoogleStaticMap from "../../shared/OwGoogleStaticMap/OwGoogleStaticMap";
import IGetContactsDTO from "../../../models/DTO/IGetContactsDTO";
import OwLabel from "../../shared/OwLabel/OwLabel";
import IntegrationsConnectedBar from "../../shared/IntegrationsConnectedBar/IntegrationsConnectedBar";
import INotification from "../../../models/INotification";
import IDeleteContactDTO from "../../../models/DTO/IDeleteContactDTO";
import AddressDisplay from "../../shared/AddressDisplay/AddressDisplay";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import OwTitle from "../../shared/OwTitle/OwTitle";
import SupplierContext from "../../../contexts/SupplierContext";
import UiContext from "../../../contexts/UiContext";
import { SocketContext } from "../../../contexts/SocketContext";
import { ConfirmDeleteOverlayFn } from "../../shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import IOnGetVendorDTO from "../../../models/DTO/IOnGetVendorDTO";
import IVendorModel from "../../../models/IVendorModel";
import IGetVendorDTO from "../../../models/DTO/IGetVendorDTO";
import { OwDeleteEditActions } from "../../shared/OwDeleteEditActions/OwDeleteEditActions";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import SupplierService from "../../../services/SupplierService";
import breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import { ClickToCopy } from "@components/shared/ClickToCopy/ClickToCopy";
import OwPill from "../../shared/OwPill/OwPill";

type Props = {};

export const Contacts: React.FC<Props> = (props: Props) => {
    const history = useNavigate();
    const match = useMatch("/account/:guid/contacts");
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);
    const socket = useContext(SocketContext)!;

    const guid = match?.params.guid || "";

    const [vendorGuid, setVendorGuid] = useState<string>(guid);
    const [contacts, setContacts] = useState<IContact[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [tableCols, setTableCols] = useState<IOwTableCols[]>([]);
    const [vendorName, setVendorName] = useState<string>();
    const [vendorModel, setVendorModel] = useState<IVendorModel>();
    const [savingContact, setSavingContact] = useState<boolean>(false);
    const [selectedContact, setSelectedContact] = useState<IContact>();
    const [deleteOverlay, setDeleteOverlay] = useState<React.Ref<HTMLElement>>();
    const [sourceElement, setSourceElement] = useState<any>();
    const [crumbs, setCrumbs] = useState<IBreadcrumbs[]>([
        { title: "Dashboard", url: "/dashboard" },
        {
            title: SupplierService.updateStringWithVendorOverride("vendors", supplierModel),
            url: SupplierService.updateStringWithVendorOverride("/vendors", supplierModel),
        },
    ]);

    const onGetContacts = (data: IOnGetContactsDTO) => {
        setContacts(data.contacts);
        setVendorName(data.vendorName);
    };

    const onDeleteContact = () => {
        const notification: INotification = {
            message: "successfully_deleted_contact",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);

        getContacts();
    };

    const onGetVendor = (data: IOnGetVendorDTO) => {
        setVendorModel(data.vendor);
        uiContext.setActivityIndicator(false);
    };

    useEffect(() => {
        if (contacts && vendorModel) {
            setIsInitialLoad(false);
            setIsLoading(false);
            setTableCols(getTableCols);

            if (crumbs.length < 3) {
                const updatedCrumbs = [...crumbs];
                updatedCrumbs.push({ title: `${vendorModel.name} Contacts`, url: `/account/${guid}/contacts`, skipTranslate: true });
                setCrumbs(updatedCrumbs);
            }

            document.title = `Orderwerks >> ${vendorModel.name} >> Contacts`;
        }
    }, [contacts, vendorModel]);

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.ContactsOnGetContacts, fn: onGetContacts },
        { endpoint: EndpointsEnum.ContactsOnDeleteContact, fn: onDeleteContact },
        { endpoint: EndpointsEnum.VendorsOnGetVendor, fn: onGetVendor },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getContacts();
        getVendor();

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const getTableCols = (): IOwTableCols[] => {
        const cols: IOwTableCols[] = [
            {
                title: "",
                valueProp: "",
                renderFn: renderActionsFn,
                renderFnEmitsRow: true,
                className: "w-10r",
            },
            {
                title: "name",
                valueProp: "name",
                renderFn: renderNameFn,
                renderFnEmitsRow: true,
            },
            {
                title: "address",
                valueProp: "",
                renderFn: renderAddressFn,
                renderFnEmitsRow: true,
            },
        ];

        return cols;
    };

    const selectElementToDelete = (row: IContact, index: number) => {
        setSelectedContact(row);

        const elem = `row-${index}`;
        setSourceElement(elem);
    };

    const getVendor = () => {
        const payload: IGetVendorDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid,
        };

        socket.emit(EndpointsEnum.VendorsGetVendor, payload);

        uiContext.setActivityIndicator(true);
    };

    const deleteContact = (row: IContact) => {
        const payload: IDeleteContactDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            contactGuid: row.guid,
        };

        socket.emit(EndpointsEnum.ContactsDeleteContact, payload);

        setSelectedContact(undefined);
        setSourceElement(null);
    };

    const renderActionsFn = (row: IContact, index: number) => {
        return (
            <div>
                <OwDeleteEditActions
                    isEditMode={false}
                    onTrashClickFn={() => {
                        selectElementToDelete(row, index);
                    }}
                    onEditClickFn={() => {
                        showContactSidebar(row);
                    }}
                />
            </div>
        );
    };

    const renderNameFn = (row: IContact) => {
        return (
            <div className={"d-flex flex-column"}>
                <div className={"d-flex align-items-center"}>
                    <span>{row.name}</span>
                    {row.is_primary && <OwPill title={"primary"} className={"ms-2"} />}
                </div>
                {!!row.email?.length && (
                    <div className={"d-flex align-items-center"}>
                        <OwLabel title={"email"} postText={":"} className={"me-2"} isBold={true} />
                        <ClickToCopy text={row.email} noFlexGrow={true}>
                            <a href={`mailto:${row.email}`}>{row.email}</a>
                        </ClickToCopy>
                    </div>
                )}
                {!!row.phone?.length && (
                    <div className={"d-flex align-items-center"}>
                        <OwLabel title={"phone"} postText={":"} className={"me-2"} isBold={true} />
                        <ClickToCopy text={row.phone} noFlexGrow={true}>
                            <a href={`tel:${row.phone}`}>{row.phone}</a>
                        </ClickToCopy>
                    </div>
                )}
                {!!row.mobile?.length && (
                    <div className={"d-flex align-items-center"}>
                        <OwLabel title={"mobile"} postText={":"} className={"me-2"} isBold={true} />
                        <ClickToCopy text={row.mobile} noFlexGrow={true}>
                            <a href={`tel:${row.mobile}`}>{row.mobile}</a>
                        </ClickToCopy>
                    </div>
                )}
                <div className={"d-flex"}>
                    <IntegrationsConnectedBar
                        position={"left"}
                        supplierModel={supplierModel}
                        zohoConnected={row.zoho_id}
                        hubspotConnected={row.hubspot_id}
                        showIntegrations={{
                            zoho: true,
                            hubspot: true,
                        }}
                    />
                </div>
            </div>
        );
    };

    const renderAddressFn = (row: IContact) => {
        return (
            <div className={"d-flex"}>
                {row.Address?.city && row.Address?.state ? (
                    <OwGoogleStaticMap className={"me-2"} address={row.Address} size={"120x120"} />
                ) : null}
                <AddressDisplay address={row.Address} />
            </div>
        );
    };

    const goBack = () => {
        history(-1);
    };

    const getContacts = () => {
        if (vendorGuid) {
            const payload: IGetContactsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid,
            };

            socket.emit(EndpointsEnum.ContactsGetContacts, payload);

            setIsLoading(true);
        }
    };

    const closeSidebar = () => {
        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);
    };

    const contactSuccessSave = (update?: boolean) => {
        closeSidebar();

        const notification: INotification = {
            message: "successfully_created_contact",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        if (update) {
            notification.message = "successfully_updated_contact";
        }

        uiContext.notifyFn(null, notification);

        getContacts();
    };

    const showContactSidebar = (contact?: IContact) => {
        console.log("showContactsSidebar: ", contact, vendorModel);

        if (vendorModel) {
            const sideBarComponent = (
                <ContactSidebar
                    closeSidebarFn={closeSidebar}
                    socket={socket}
                    vendorModel={vendorModel}
                    successFn={contactSuccessSave}
                    contact={contact}
                />
            );

            uiContext.setSidebarComponent(sideBarComponent);
            uiContext.toggleSidebar(true);
        }
    };

    const commitDelete = () => {
        if (selectedContact) {
            deleteContact(selectedContact);
        }
    };

    const cancelDelete = () => {
        setSelectedContact(undefined);
        setSourceElement(null);
    };

    return (
        <div className={"Contacts"}>
            <div>
                <Breadcrumbs crumbs={crumbs} />
                <div className={"d-flex align-items-center"}>
                    <div className={"d-flex"}>
                        <OwTitle title={vendorModel?.name || ""} disableTranslation={true} />
                        <OwTitle className={"ms-2"} title={"contacts"} />
                    </div>
                    <div className={"ms-2"}>
                        <OwButton
                            className={"btn-warning"}
                            title={"create"}
                            leftIconClass={"fas fa-plus"}
                            fn={() => {
                                showContactSidebar();
                            }}
                        />
                    </div>
                </div>

                <div className={"mt-2 position-relative"}>
                    <ConfirmDeleteOverlayFn
                        targetElem={sourceElement}
                        message={"confirm_delete"}
                        handleConfirm={commitDelete}
                        handleCancel={cancelDelete}
                        nestedParentElem={"account-contacts"}
                        skipMainView={true}
                    />

                    <OwTable
                        id={"account-contacts"}
                        columns={tableCols}
                        dataSet={contacts}
                        userModel={userModel}
                        supplierModel={supplierModel}
                        dataFetch={isLoading}
                        initialData={isInitialLoad}
                    />
                </div>
            </div>
        </div>
    );
};
